import React, { Component } from 'react';
import PropTypes from 'prop-types';
import SelectedSkuBin from '@cimpress-technology/ff-react-components/lib/SkuSelector/SelectedSkuBin/SelectedSkuBin';
import { css } from 'emotion';
import { SkuInfoShape } from '../createContract/shared/SkuCard/SkuCard';
import {
  fetchRequiredInputsForItems,
  actions as termActions,
  selectors as termSelectors
} from '../../../reducers/terms';
import { connect } from 'react-redux';
import DiffPricingGrid from './DiffPricingGrid';
import omit from 'lodash/omit';
import keyBy from 'lodash/keyBy';
import memoizeOne from 'memoize-one';
import { DiffStatus } from './ItemChanges';
import DiffStatusSkuCard from './DiffStatusSkuCard';
import TermPricingGrid from '../viewTermRevision/TermPricingGrid';
import { NavTab, NavTabItem } from '@cimpress/react-components';
import uniqBy from 'lodash/uniqBy';
import { shapes } from '@cimpress/react-components';
import { skuInfoToPriceModelAndSkuRows } from '../../../utilities/generateCsvRowsOverrideFunctions';
import { getRuleSet } from '../../../services/SkuRuleset';

const { Robot } = shapes;

const selectedSkuBinStyle = css`
  position: relative;
  right: 10px;
`;

const DiffTabs = {
  CHANGES: 'CHANGES',
  NO_CHANGES: 'NO_CHANGES'
};

class ItemsDiffDisplay extends Component {
  static defaultProps = {
    fromItems: {},
    toItems: {}
  };

  static propTypes = {
    from: PropTypes.object.isRequired,
    to: PropTypes.object.isRequired,
    diffInfos: PropTypes.array.isRequired,
    fromItems: PropTypes.objectOf(SkuInfoShape.isRequired).isRequired,
    toItems: PropTypes.objectOf(SkuInfoShape.isRequired).isRequired,
    showSkuRulesetNotFoundError: PropTypes.func
  };

  state = {
    selectedSku: null,
    selectedTab: DiffTabs.CHANGES
  };

  componentDidMount() {
    const { changedItemsArray } = this.getAllItemsWithDiffStatus(
      this.props.fromItems,
      this.props.toItems,
      this.props.diffInfos
    );
    const preSelectedSku = changedItemsArray[0] || {};
    this.selectSku(preSelectedSku);

    this.props.fetchPriceModelsForTermItems(this.props.fromItems, this.props.from.termId, this.props.from.revisionId);
    this.props.fetchPriceModelsForTermItems(this.props.toItems, this.props.to.termId, this.props.to.revisionId);
    this.props.getRequiredInputs(changedItemsArray);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const fromHasNewItems =
      Object.values(this.props.fromItems).filter(({ sku }) => !prevProps.fromItems[sku]).length > 0;
    const toHasNewItems = Object.values(this.props.toItems).filter(({ sku }) => !prevProps.toItems[sku]).length > 0;
    if (fromHasNewItems || toHasNewItems) {
      const { changedItemsArray } = this.getAllItemsWithDiffStatus(
        this.props.fromItems,
        this.props.toItems,
        this.props.diffInfos
      );
      const preSelectedSku = changedItemsArray[0] || {};
      this.selectSku(preSelectedSku);

      this.props.fetchPriceModelsForTermItems(this.props.fromItems, this.props.from.termId, this.props.from.revisionId);
      this.props.fetchPriceModelsForTermItems(this.props.toItems, this.props.to.termId, this.props.to.revisionId);
    }
  }

  selectSku = skuInfo => {
    if (skuInfo) {
      getRuleSet(skuInfo.sku)
        .then(skuRuleSets => {
          const authoritativeRuleSet = skuRuleSets.filter(each => each.authoritative);
          if (authoritativeRuleSet) {
            const skuRuleSet = authoritativeRuleSet[0];
            this.setState({ skuRuleSet });
            this.props.clearSkuRuleSetNotFoundError(null, skuInfo);
          } else {
            this.props.showSkuRulesetNotFoundError('Ruleset not found', skuInfo);
          }
        })
        .catch(error => {
          this.props.showSkuRulesetNotFoundError(error, skuInfo);
        });
    }
    this.setState({ selectedSku: skuInfo.sku });
  };

  getAllItemsWithDiffStatus = memoizeOne((fromItems, toItems, diffInfos) => {
    // NOTE (brlee): diffInfos = [{ from: null, to: {...}, diffStatus: DiffStatus.ADDED }, ...].
    // I omit the priceModel because the purpose of this function is to figure out the diff information and sku information, not the pricemodel information.
    // The price model information can be incorrect when 'from' and 'to' properties are both available (in the case of diffStatus) and the price model differs between these two contract revisions.
    // I filter the mapped array with valid objects because the mapped array could look like [{}, null, null, {}] afterwards.

    const allFroms = diffInfos
      .map(({ from, diffStatus }) => ({
        ...omit(from, 'priceModel'),
        diffStatus
      }))
      .filter(o => !!o && o.sku);

    const allTos = diffInfos
      .map(({ to, diffStatus }) => ({
        ...omit(to, 'priceModel'),
        diffStatus
      }))
      .filter(o => !!o && o.sku);

    let all = [...allFroms, ...allTos];

    all = uniqBy(all, 'sku');

    all.sort((a, b) => {
      // sort the items in this order so they get displayed this way.
      const sortOrderMap = {
        [DiffStatus.ADDED]: 1,
        [DiffStatus.CHANGED]: 2,
        [DiffStatus.REMOVED]: 3,
        [DiffStatus.UNCHANGED]: 4
      };

      if (sortOrderMap[a.diffStatus] < sortOrderMap[b.diffStatus]) {
        return -1;
      }

      if (sortOrderMap[a.diffStatus] > sortOrderMap[b.diffStatus]) {
        return 1;
      }

      return 0;
    });

    const changedItemsArray = all.filter(item => item.diffStatus !== DiffStatus.UNCHANGED);

    const unchangedItemsArray = all.filter(item => item.diffStatus === DiffStatus.UNCHANGED);

    return { changedItemsArray, unchangedItemsArray, allItemsArray: all };
  });

  renderSkuCard = options => {
    const { isSkuClicked } = options;
    return <DiffStatusSkuCard clickable isClicked={isSkuClicked(options.skuInfo.sku)} {...options} />;
  };

  render() {
    const { fromItems, toItems, from, to, getSelectedSkuPriceModelInfo, diffInfos } = this.props;
    const { selectedSku, selectedTab, skuRuleSet } = this.state;

    const { changedItemsArray, unchangedItemsArray, allItemsArray } = this.getAllItemsWithDiffStatus(
      fromItems,
      toItems,
      diffInfos
    );

    if (changedItemsArray.length === 0) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
          <h4>There are no item changes</h4>
          <Robot bsStyle="info" />
        </div>
      );
    }

    const selectedSkuInfoForFromTerm = fromItems[selectedSku];
    const selectedSkuInfoForToTerm = toItems[selectedSku];

    const selectedSkuPriceModelInfoForFromTerm =
      selectedSkuInfoForFromTerm &&
      selectedSkuInfoForFromTerm.priceModel &&
      getSelectedSkuPriceModelInfo(
        selectedSkuInfoForFromTerm.priceModel.id,
        selectedSkuInfoForFromTerm.priceModel.revisionId
      );

    const selectedSkuPriceModelInfoForToTerm =
      selectedSkuInfoForToTerm &&
      selectedSkuInfoForToTerm.priceModel &&
      getSelectedSkuPriceModelInfo(
        selectedSkuInfoForToTerm.priceModel.id,
        selectedSkuInfoForToTerm.priceModel.revisionId
      );

    // Pass in list, but not items because skuInfoToPriceModelAndSkuRows can't handle comparison info
    const changedSkuCsv = () => {
      return skuInfoToPriceModelAndSkuRows(changedItemsArray);
    };

    const unchangedSkuCsv = () => {
      return skuInfoToPriceModelAndSkuRows(unchangedItemsArray);
    };

    const changesTabContent = (
      <SelectedSkuBin
        style={{ minHeight: 300, height: 700 }}
        skus={keyBy(changedItemsArray, 'sku')}
        clickable
        isSkuClicked={sku => sku === selectedSku}
        onAction={this.selectSku}
        locale={'en'}
        skuCardCustomRenderFn={this.renderSkuCard}
        generateCsvRowsOverride={changedSkuCsv}
      />
    );

    const noChangesTabContent = selectedTab === DiffTabs.NO_CHANGES && (
      <SelectedSkuBin
        style={{ minHeight: 300, height: 700 }}
        skus={keyBy(unchangedItemsArray, 'sku')}
        clickable
        isSkuClicked={sku => sku === selectedSku}
        onAction={this.selectSku}
        locale={'en'}
        skuCardCustomRenderFn={this.renderSkuCard}
        generateCsvRowsOverride={unchangedSkuCsv}
      />
    );

    const tabContent = selectedTab === DiffTabs.CHANGES ? changesTabContent : noChangesTabContent;

    const selectedSkuBin = (
      <div className={`${selectedSkuBinStyle} col-xs-12 col-md-3`}>
        <div>
          <NavTab>
            <NavTabItem active={selectedTab === DiffTabs.CHANGES}>
              <button onClick={() => this.setState({ selectedTab: DiffTabs.CHANGES })}>
                Changes ({changedItemsArray.length})
              </button>
            </NavTabItem>
            <NavTabItem active={selectedTab === DiffTabs.NO_CHANGES} disabled={unchangedItemsArray.length === 0}>
              <button
                onClick={
                  unchangedItemsArray.length === 0 ? null : () => this.setState({ selectedTab: DiffTabs.NO_CHANGES })
                }>
                No Changes ({unchangedItemsArray.length})
              </button>
            </NavTabItem>
          </NavTab>
          {tabContent}
        </div>
      </div>
    );

    let pricingGridWrapper = null;

    if (selectedSku && to) {
      const selectedSkuInfo = allItemsArray.find(item => item.sku === selectedSku);
      switch (selectedSkuInfo && selectedSkuInfo.diffStatus) {
        case DiffStatus.CHANGED:
          pricingGridWrapper = (
            <div className="col-xs-12 col-md-9">
              <DiffPricingGrid
                from={from}
                to={to}
                fromPriceModelInfo={selectedSkuPriceModelInfoForFromTerm}
                toPriceModelInfo={selectedSkuPriceModelInfoForToTerm}
                skuRuleSet={skuRuleSet}
                sku={selectedSku}
              />
            </div>
          );
          break;
        case DiffStatus.ADDED:
        case DiffStatus.UNCHANGED:
          pricingGridWrapper = (
            <div className="col-xs-12 col-md-9">
              <TermPricingGrid
                termRevision={to}
                skuInfo={selectedSkuInfoForToTerm}
                priceModelInfo={selectedSkuPriceModelInfoForToTerm}
                skuRuleSet={skuRuleSet}
              />
            </div>
          );
          break;
        default:
          pricingGridWrapper = null;
      }
    }

    return (
      <div>
        {selectedSkuBin}
        {pricingGridWrapper}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    getSelectedSkuPriceModelInfo: (id, revisionId) =>
      termSelectors.getPriceModel(state, id, revisionId) || { id, revisionId }
  };
};
const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    getRequiredInputs: sku => dispatch(fetchRequiredInputsForItems(sku)),
    fetchPriceModelsForTermItems: termActions.fetchPriceModelsForTermItems,
    showSkuRulesetNotFoundError: (error, skuInfo) =>
      dispatch(termActions.setSkuRuleSetLoadingStatus(skuInfo, true, error)),
    clearSkuRuleSetNotFoundError: skuInfo => dispatch(termActions.setSkuRuleSetLoadingStatus(skuInfo, false, null))
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ItemsDiffDisplay);
